import { matchPath, Redirect, Route, Switch, useLocation } from 'react-router'

import { ProtectedRoute } from '@webapp/app'
import { useAppRoutes, useFeatureFlag } from '@webapp/hooks'
import { OrderApprovalTabs } from '@webapp/modules/purchasing/entities/enums/OrderApprovalTabEnum'
import { OrderStatusPage } from '@webapp/pages'
import { Permissions } from '@webapp/platform/iam'
import { OrderApprovalsPage } from './OrderApprovalsPage'
import { OrderDetailsPage } from './OrderDetailsPage'
import { OrderRequestPage } from './OrderRequestPage'
import {
  type LegacyOrderApprovalsPageStatus,
  LegacyOrderApprovalsPageStatuses,
  OrderDetailsPageTabs,
  OrderStatusPageStatuses,
} from './types'

const isLegacyOrderApprovalsPageStatus = (
  value: string
): value is LegacyOrderApprovalsPageStatus =>
  Object.values(LegacyOrderApprovalsPageStatuses).some(
    (status) => status === value
  )

const orderStatusFormat = Object.values(OrderStatusPageStatuses).join('|')
const orderDetailsTabFormat = Object.values(OrderDetailsPageTabs).join('|')
const orderApprovalStatusFormat = Object.values(OrderApprovalTabs).join('|')

export const OrderRouter = () => {
  const { brp, url } = useAppRoutes()
  const location = useLocation()

  const RFO_AUTOMATION = useFeatureFlag('RFO_AUTOMATION')
  const PUR_4521_ORDER_APPROVAL_STATUS_POLISH = useFeatureFlag(
    'PUR_4521_ORDER_APPROVAL_STATUS_POLISH'
  )

  const approvalStatusFromPath =
    matchPath<{ status?: string }>(location.pathname, {
      path: brp('OrderApprovalsPage', { status: ':status?' }),
    })?.params.status ?? ''

  const orderDetailsPagePermissions = [
    Permissions.ADD_ORDER,
    Permissions.PROCURE_ADD_PO,
    Permissions.ACCOUNTS_PAYABLE_ADD_BILL,
    Permissions.ACCOUNTS_PAYABLE_CREATE_PAYMENTS,
    Permissions.APPROVE_ORDER,
    /**
     * @legacy
     * remove with `legacy-payments` switch
     */
    Permissions.LEGACY_PAYMENT,
  ]

  return (
    <Switch>
      {/* Order Request */}
      {RFO_AUTOMATION ? (
        <ProtectedRoute
          path={brp('OrderRequestPage')}
          component={OrderRequestPage}
          permissions={[Permissions.ADD_ORDER]}
        />
      ) : (
        <Redirect
          exact
          from={brp('OrderRequestPage')}
          to={url('LegacyOrderRequestPage')}
        />
      )}

      {/* Order Status */}
      <Redirect
        exact
        from={brp('OrderStatusPage', {
          status: '',
        })}
        to={url('OrderStatusPage', {
          status: OrderStatusPageStatuses.PENDING,
        })}
      />

      <ProtectedRoute
        path={brp('OrderStatusPage', {
          status: `:status(${orderStatusFormat})`,
        })}
        component={OrderStatusPage}
        navigationPermissions={['request__order']}
      />

      {/*
       * Order Approvals
       *
       * Route conflict — insertion order is load-bearing: this block MUST
       * precede both the unconstrained `/orders/:uuid` detail routes below and
       * the `path='*'` catch-all, or `/orders/approvals` exact-matches the
       * detail route with `uuid = 'approvals'` and `/orders/approvals/pending`
       * fails the tab regex and falls into the catch-all.
       */}
      {!PUR_4521_ORDER_APPROVAL_STATUS_POLISH && (
        <Redirect
          from={brp('OrderApprovalsPage', { status: ':status?' })}
          to={`${url('LegacyOrderApprovalsPage', {
            status: isLegacyOrderApprovalsPageStatus(approvalStatusFromPath)
              ? approvalStatusFromPath
              : LegacyOrderApprovalsPageStatuses.PENDING,
          })}${location.search}`}
        />
      )}
      <ProtectedRoute
        path={brp('OrderApprovalsPage', {
          status: `:status(${orderApprovalStatusFormat})`,
        })}
        component={OrderApprovalsPage}
        permissions={[Permissions.APPROVE_ORDER]}
        featureFlags={['PUR_4521_ORDER_APPROVAL_STATUS_POLISH']}
      />

      {/*
       * Empty or unknown approval status falls through the constrained route
       * above and redirects to the default pending tab.
       */}
      {PUR_4521_ORDER_APPROVAL_STATUS_POLISH && (
        <Redirect
          from={brp('OrderApprovalsPage', { status: ':status?' })}
          to={`${url('OrderApprovalsPage', { status: 'pending' })}${location.search}`}
        />
      )}

      {/* Order Details */}
      <ProtectedRoute
        exact
        path={brp('OrderDetailsPage', { uuid: ':uuid' })}
        component={OrderDetailsPage}
        permissions={orderDetailsPagePermissions}
      />
      <ProtectedRoute
        exact
        path={brp('OrderDetailsPage', {
          uuid: ':uuid',
          tab: `:tab(${orderDetailsTabFormat})`,
        })}
        component={OrderDetailsPage}
        permissions={orderDetailsPagePermissions}
      />

      {/* Fallback for /orders/ */}
      <Route
        path='*'
        render={() => (
          <Redirect
            to={url('OrderStatusPage', {
              status: OrderStatusPageStatuses.PENDING,
            })}
          />
        )}
      />
    </Switch>
  )
}
